<template>
  <section v-if="imageSrc" class="parallax-section">
    <div
      class="img-holder"
      :class="{ 'img-holder--mobile-parallax': useMobileParallax }"
      ref="holderRef"
      :data-image="useMobileParallax ? undefined : imageSrc"
      :style="mobileBackgroundStyle"
    ></div>
  </section>
</template>

<script>
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'
import { getMyPortalImageProcessUrl, getImagePreset, isMyPortalImageId, loadProcessImageUrl } from '../../lib/helper.js'
import { MOBILE_MAX_WIDTH, isMobileViewport } from '../../lib/viewport.js'

const HOLDER_MAX_HEIGHT_DESKTOP = 400
const HOLDER_MAX_HEIGHT_MOBILE = 240
const PARALLAX_PRESET = getImagePreset('parallax')

function getHolderMaxHeight() {
  return isMobileViewport() ? HOLDER_MAX_HEIGHT_MOBILE : HOLDER_MAX_HEIGHT_DESKTOP
}

function destroyImageScroll(holderEl) {
  if (!holderEl || typeof window.$ === 'undefined') return
  const $holder = window.$(holderEl)
  if (!$holder.data('plugin_imageScroll')) return
  try {
    $holder.imageScroll('destroy')
  } catch (_) {
    /* ignore */
  }
}

function initImageScroll(holderEl) {
  if (!holderEl || typeof window.$ === 'undefined') return
  const $ = window.$
  const $holder = $(holderEl)
  if ($holder.data('plugin_imageScroll')) {
    try {
      $holder.imageScroll('destroy')
    } catch (_) {
      /* ignore */
    }
  }
  $holder.imageScroll({
    holderMaxHeight: getHolderMaxHeight(),
    mediaWidth: PARALLAX_PRESET.width,
    mediaHeight: PARALLAX_PRESET.height,
  })
}

export default {
  props: {
    src: String,
    imageId: String
  },
  setup(props) {
    const imageSrc = ref('')
    const holderRef = ref(null)
    const useMobileParallax = ref(isMobileViewport())
    let mediaQuery = null
    let onViewportChange = null

    const mobileBackgroundStyle = computed(() => {
      if (!useMobileParallax.value || !imageSrc.value) return undefined
      return { backgroundImage: `url(${imageSrc.value})` }
    })

    const refreshParallaxMode = async () => {
      useMobileParallax.value = isMobileViewport()
      if (!holderRef.value || !imageSrc.value) return

      await nextTick()

      if (useMobileParallax.value) {
        destroyImageScroll(holderRef.value)
        return
      }

      initImageScroll(holderRef.value)
    }

    const loadImage = async () => {
      if (!props.imageId || !isMyPortalImageId(props.imageId)) {
        imageSrc.value = ''
        return
      }

      const processUrl = getMyPortalImageProcessUrl(props.imageId, 'parallax')
      const loaded = await loadProcessImageUrl(processUrl)
      imageSrc.value = loaded
      if (!loaded) return

      await refreshParallaxMode()
    }

    watch(() => props.imageId, (newId) => {
      if (newId) loadImage()
    }, { immediate: true })

    onMounted(() => {
      mediaQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`)
      onViewportChange = () => {
        refreshParallaxMode()
      }
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', onViewportChange)
      } else {
        mediaQuery.addListener(onViewportChange)
      }
    })

    onUnmounted(() => {
      destroyImageScroll(holderRef.value)
      if (!mediaQuery || !onViewportChange) return
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', onViewportChange)
      } else {
        mediaQuery.removeListener(onViewportChange)
      }
    })

    return {
      imageSrc,
      holderRef,
      useMobileParallax,
      mobileBackgroundStyle
    }
  }
}
</script>
